"use client";

import { formatMaxBucks } from "@mot-cards/types";
import { cn, formatCentsToCurrency } from "@mot-cards/ui";
import { maskBalance, useBalanceHidden } from "@/hooks/useBalanceHidden";

/** The user's wallet balance as text, masked while hidden. */
export function BalanceAmount({ cents }: { cents: number }) {
  const { hidden } = useBalanceHidden();
  return <>{maskBalance(formatCentsToCurrency(cents), hidden)}</>;
}

/** Cash balance plus any Max Bucks ("$50.00 · MB 25.00"), masked while hidden. */
export function BalanceWithMaxBucks({ cents, maxBucksCents = 0 }: { cents: number; maxBucksCents?: number }) {
  const { hidden } = useBalanceHidden();
  const cash = maskBalance(formatCentsToCurrency(cents), hidden);
  return <>{maxBucksCents > 0 ? `${cash} · ${maskBalance(formatMaxBucks(maxBucksCents), hidden)}` : cash}</>;
}

/** The eye on a balance pill: hides/shows the wallet balance sitewide. */
export function BalanceVisibilityToggle({ className }: { className?: string }) {
  const { hidden, toggle } = useBalanceHidden();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={hidden ? "Show balance" : "Hide balance"}
      aria-pressed={hidden}
      title={hidden ? "Show balance" : "Hide balance"}
      className={cn(
        "grid size-7 shrink-0 place-items-center rounded-full text-mot-grey-light transition-colors hover:text-mot-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mot-teal",
        className,
      )}
    >
      <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
        {hidden ? <path d="M4 4l16 16" /> : null}
      </svg>
    </button>
  );
}
